import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useMemo, useState } from "react";
import CloseDialogButton from "../../../components/CloseDialogButton";
import { actionBtnSx } from "./actionButtonStyles";
import { getAllBrands } from "../../../services/brandService";
import {
  createRestaurant,
  getAllRestaurants,
  updateRestaurant,
} from "../../../services/restaurantService";

const blank = {
  restaurant_name: "",
  email: "",
  brand_id: "",
  address: "",
  phone: "",
  license_end_date: "",
  license_pdf: "",
};

const today = () => new Date().toISOString().slice(0, 10);

const toDateInput = (val) => (val ? String(val).slice(0, 10) : "");

const RestaurantsPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [restaurants, setRestaurants] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filterBrand, setFilterBrand] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([getAllRestaurants(), getAllBrands()]);
      setRestaurants(r.data?.data || []);
      setBrands(b.data?.data || []);
    } catch (e) {
      enqueueSnackbar(
        e.response?.data?.message || "Failed to load restaurants",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const brandName = (id) => brands.find((b) => b.id === id)?.name || "—";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return restaurants.filter((r) => {
      if (filterBrand !== "all" && r.brand_id !== filterBrand) return false;
      if (q) {
        const hay = `${r.restaurant_name || ""} ${r.email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [restaurants, filterBrand, search]);

  const openCreate = () => {
    setForm({ ...blank, brand_id: brands[0]?.id || "" });
    setEditing({});
  };

  const openEdit = (r) => {
    setForm({
      restaurant_name: r.restaurant_name || "",
      email: r.email || "",
      brand_id: r.brand_id || "",
      address: r.address || "",
      phone: r.phone || "",
      license_end_date: toDateInput(r.license_end_date),
      license_pdf: r.license_pdf || "",
    });
    setEditing(r);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        brand_id: form.brand_id || null,
        license_end_date: form.license_end_date || null,
      };
      if (editing?.id) {
        await updateRestaurant(editing.id, payload);
        enqueueSnackbar("Restaurant updated", { variant: "success" });
      } else {
        await createRestaurant({ ...payload, license_start_date: today() });
        enqueueSnackbar("Restaurant created", { variant: "success" });
      }
      setEditing(null);
      load();
    } catch (e) {
      enqueueSnackbar(
        e.response?.data?.message || "Failed to save restaurant",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const buildLoginUrl = (r) => {
    const base = (
      process.env.REACT_APP_PUBLIC_URL || "https://app.holomenu.net"
    ).replace(/\/$/, "");
    return `${base}/login?restaurant_name=${encodeURIComponent(
      r.restaurant_name || ""
    )}`;
  };

  const copyLoginLink = async (r) => {
    const url = buildLoginUrl(r);
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar(`Login link copied for ${r.restaurant_name}`, {
        variant: "success",
      });
    } catch {
      // Fallback for non-secure contexts / older browsers
      window.prompt("Copy this link:", url);
    }
  };

  const licenseStatus = (r) => {
    if (!r.license_end_date) return null;
    const end = new Date(r.license_end_date);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    if (days < 0) return { color: "error", label: "Expired" };
    if (days <= 14) return { color: "warning", label: `${days}d left` };
    return { color: "success", label: `${days}d left` };
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Restaurants <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>(branches)</Typography>
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={brands.length === 0}
        >
          New restaurant
        </Button>
      </Box>

      {brands.length === 0 && !loading && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, color: "text.secondary" }}>
          <Typography variant="body2">
            Create a brand first before adding restaurants.
          </Typography>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
        >
          <TextField
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              label="Brand"
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
            >
              <MenuItem value="all">All brands</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "rgba(72,102,78,0.06)" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Restaurant</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>License</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ color: "text.secondary", py: 3 }}
                  >
                    No restaurants match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => {
                const status = licenseStatus(r);
                return (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Typography fontWeight={600}>
                        {r.restaurant_name || "(unnamed)"}
                      </Typography>
                    </TableCell>
                    <TableCell>{brandName(r.brand_id)}</TableCell>
                    <TableCell
                      sx={{
                        color: r.email ? "text.primary" : "text.disabled",
                      }}
                    >
                      {r.email || "—"}
                    </TableCell>
                    <TableCell>
                      {status ? (
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          no license
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => copyLoginLink(r)}
                        aria-label="Copy login link"
                        title="Copy login link"
                        disabled={!r.restaurant_name}
                        sx={actionBtnSx("tertiary")}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openEdit(r)}
                        aria-label="Edit"
                        title="Edit"
                        sx={actionBtnSx("primary")}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 7 }}>
          {editing?.id ? "Edit restaurant" : "New restaurant"}
          <CloseDialogButton
            onClose={() => setEditing(null)}
            loading={saving}
          />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Restaurant name"
              value={form.restaurant_name}
              onChange={(e) =>
                setForm({ ...form, restaurant_name: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Brand</InputLabel>
              <Select
                label="Brand"
                value={form.brand_id || ""}
                onChange={(e) =>
                  setForm({ ...form, brand_id: e.target.value })
                }
              >
                {brands.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
            />
            <Typography variant="subtitle2" sx={{ pt: 1 }}>
              License
            </Typography>
            <TextField
              label="End date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.license_end_date}
              onChange={(e) =>
                setForm({ ...form, license_end_date: e.target.value })
              }
              fullWidth
              helperText={
                editing?.id
                  ? "Start date is set when the license is first created."
                  : "Start date will be set to today."
              }
            />
            <TextField
              label="License PDF (URL)"
              value={form.license_pdf}
              onChange={(e) =>
                setForm({ ...form, license_pdf: e.target.value })
              }
              fullWidth
              placeholder="https://…"
              helperText="Paste a URL to the license PDF (upload via the Files form)."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.email || !form.brand_id}
          >
            {saving ? "Saving…" : editing?.id ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default RestaurantsPanel;
